import React, { FC, useState } from 'react'
import Popover from '@mui/material/Popover'
import Box from '@mui/material/Box'
import { ENTITY_TYPES, usePopoverActions } from '@sales-center/common'
import { AIIcon } from '@user/components/DetailPages/sections/EntityName/MeasureAI/Icons/AiIcon'
import { useHandlers } from './hooks/useHandlers'
import { Measure } from '@user/components/DetailPages/sections/EntityName/MeasureAI/Measure'
import { PropertyTitleTypography } from './PropertyTitleTypography'
import type { PopoverOrigin } from '@mui/material/Popover'

interface Props {
  entityType: ENTITY_TYPES
  entityId: number
  hasValidPropertyAddress?: boolean
}

const EntityPropertyDetailsTitle: FC<Props> = ({
  entityId,
  entityType,
  hasValidPropertyAddress = false,
}) => {
  const popoverActions = usePopoverActions()
  const { popoverActionsRef, updatePosition } = popoverActions

  const { handleClick, onClose, anchorEl, propertyDetailsOpen } = useHandlers()

  const [origin, setOrigin] = useState<{
    anchor: PopoverOrigin
    transform: PopoverOrigin
  }>({
    anchor: { vertical: 'bottom', horizontal: 'center' },
    transform: { vertical: 'top', horizontal: 'center' },
  })

  const handleDynamicClick = (e: React.MouseEvent<HTMLElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const popoverHeight = 300
    const spaceBelow = window.innerHeight - rect.bottom

    if (spaceBelow < popoverHeight) {
      setOrigin({
        anchor: { vertical: 'top', horizontal: 'center' },
        transform: { vertical: 'bottom', horizontal: 'center' },
      })
    } else {
      setOrigin({
        anchor: { vertical: 'bottom', horizontal: 'center' },
        transform: { vertical: 'top', horizontal: 'center' },
      })
    }

    handleClick(e)
  }

  if (!hasValidPropertyAddress) return null

  return (
    <>
      <Box
        component="span"
        mr={2.9}
        data-test-id={`entity-property-details-${entityId}`}
      >
        <AIIcon />
        <PropertyTitleTypography component="span" onClick={handleDynamicClick}>
          Property Details
        </PropertyTitleTypography>
      </Box>

      <Popover
        id={entityId.toString()}
        open={propertyDetailsOpen}
        onClose={onClose}
        anchorEl={anchorEl}
        action={popoverActionsRef}
        TransitionProps={{
          onEntered: updatePosition,
        }}
        anchorOrigin={origin.anchor}
        transformOrigin={origin.transform}
      >
        <Measure
          entityId={entityId}
          entityType={entityType}
          variant="card"
          onClose={onClose}
        />
      </Popover>
    </>
  )
}

export default EntityPropertyDetailsTitle
